import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Eye,
  FileText,
  MessageSquare,
  MoreVertical,
  Pencil,
  PenLine,
  Plus,
  Search,
  Users,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Menu } from '@/components/ui/Menu';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { PercentRing } from '@/components/ui/PercentRing';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { downloadCsv } from '@/lib/download';
import type { DailyReportClass } from '@/types/dailyReport';
import { dailyReportsApi } from '@/features/teacher/api/dailyReports.api';
import {
  PARTICIPATION_BANDS,
  participationBand,
} from '@/features/teacher/dailyReport/constants';
import { teacherApi } from '@/features/teacher/api/teacher.api';
import { DailyAttendanceCard } from '@/features/teacher/components/dailyReport/DailyAttendanceCard';
import { DailyReportNotesPanel } from '@/features/teacher/components/dailyReport/DailyReportNotesPanel';
import { SubjectCoverageCard } from '@/features/teacher/components/dailyReport/SubjectCoverageCard';
import { formatInstituteDate } from '@/features/teacher/utils';
import { periodRange } from '@/features/teacher/components/timetable/timetableCopy';

/** What "show everything" is called in the class and subject filters. */
const ALL = 'all';

/** How many periods a page of the table holds, and what else can be chosen. */
const PAGE_SIZES = [10, 25, 50] as const;

/**
 * The screen's filters, as they sit in the address bar, so writing a class up
 * and coming back finds the same day, class, subject, search and page.
 *
 * An empty `date` means "today", which the server decides in IST — not this
 * laptop. Class and subject are uuids the server scopes; there is no fixed set
 * to check them against.
 */
type Filters = {
  date: string;
  class: string;
  subject: string;
  search: string;
  page: number;
  /** Rows per page — with the page number, or a restored page shows other rows. */
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  date: '',
  class: ALL,
  subject: ALL,
  search: '',
  page: 1,
  limit: PAGE_SIZES[0],
};

/** Class and subject are the teacher's own uuids; only the page size is a fixed set. */
const FILTER_OPTIONS = { allowed: { limit: PAGE_SIZES } };

/** Identifies one class-and-subject across the screen. Not shown to anybody. */
function classKey(entry: Pick<DailyReportClass, 'classSectionUuid' | 'subject'>): string {
  return `${entry.classSectionUuid}:${entry.subject.uuid}`;
}

/** One row of the table: a class-and-subject, with where it sits in the day. */
interface ReportRow extends DailyReportClass {
  /** The school's own numbers for the periods this entry occupies, in order. */
  periodNumbers: number[];
  serial: number;
}

/** The day before or after a `YYYY-MM-DD`, for the header's arrows. */
function shiftDay(day: string, by: -1 | 1): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + by);

  return date.toISOString().slice(0, 10);
}

/**
 * Whether a row may be opened for writing. A class still to come may not,
 * unless something was already saved against it, which can only be withdrawn.
 */
function canWriteUp(row: DailyReportClass): boolean {
  return row.hasBeenTaught || row.report !== null;
}

function writeUpTitle(row: DailyReportClass): string {
  if (!row.hasBeenTaught) {
    return row.report
      ? 'Saved before the class took place. Open it to withdraw it.'
      : 'Opens once this class has been taught';
  }
  return row.report ? 'Edit this report' : 'Write this class up';
}

/** A figure the screen has nothing behind yet. Never a nought, which is an answer. */
const NOT_YET = <span className="text-ink-3">—</span>;

/**
 * What a teacher covered in each of their classes, on one day.
 *
 * **The day's timetable is the list.** The server returns the periods the
 * caller actually has on the chosen date, already joined to whatever was
 * written against each one — so this screen never merges a timetable with a set
 * of reports in the browser, and cannot offer a class the teacher does not take
 * that day.
 *
 * Every figure on it is counted from that one payload, except attendance, which
 * is a separate record taken by a separate screen and read here through the
 * overview endpoint. Nothing is derived twice: the tiles, the coverage bars and
 * the table are three readings of the same rows.
 */
export function TeacherDailyReportPage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { date, class: classFilter, subject: subjectFilter, search, page } = filters;

  // Any change to what is listed starts the reader on page 1 again — done
  // where the change is made, so arriving does not throw a restored page away.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  const { data, isLoading, error, refetch } = useApiQuery(
    () => dailyReportsApi.day(date || undefined),
    [date],
  );

  /**
   * The day's registers, for the attendance column and the ring below it.
   *
   * A second call rather than a field on the report payload: attendance is a
   * different record with a different screen behind it, and the daily report
   * endpoint has no business reporting on it. Keyed to the date the report
   * endpoint resolved, so the two can never describe different days.
   */
  const resolvedDate = data?.date ?? '';
  const attendance = useApiQuery(
    () => (resolvedDate ? teacherApi.overview(resolvedDate) : Promise.resolve(null)),
    [resolvedDate],
  );

  const classes = useMemo(() => data?.classes ?? [], [data]);
  const sections = useMemo(() => attendance.data?.sections ?? [], [attendance.data]);

  /**
   * Only the sections on this day's timetable. The overview returns every
   * section the teacher is allotted, and a register for a class they do not
   * take that day is no part of their day.
   */
  const daySections = useMemo(() => {
    const taughtToday = new Set(classes.map((entry) => entry.classSectionUuid));
    return sections.filter((section) => taughtToday.has(section.classSectionUuid));
  }, [sections, classes]);

  const navigate = useNavigate();

  /**
   * Opens the write-up form for one class.
   *
   * The class, subject and day travel in the address rather than in state, so
   * the form can be linked to, reloaded and shared — and a teacher who lands on
   * it from a bookmark gets the same screen as one who clicked through.
   */
  const openForm = useCallback(
    (entry: DailyReportClass, mode: 'view' | 'edit') => {
      const query = new URLSearchParams({
        class: entry.classSectionUuid,
        subject: entry.subject.uuid,
        date: resolvedDate,
        ...(mode === 'view' && { mode }),
      });
      navigate(`/teacher/daily-report/entry?${query.toString()}`);
    },
    [navigate, resolvedDate],
  );

  /** Each section's register, so a row can find its own in one lookup. */
  const registerBySection = useMemo(
    () => new Map(sections.map((section) => [section.classSectionUuid, section])),
    [sections],
  );

  const classOptions = useMemo(() => {
    const byUuid = new Map(classes.map((entry) => [entry.classSectionUuid, entry.displayName]));

    return [
      { value: ALL, label: `All my classes (${byUuid.size})` },
      ...[...byUuid].map(([value, label]) => ({ value, label })),
    ];
  }, [classes]);

  const subjectOptions = useMemo(() => {
    const byUuid = new Map(classes.map((entry) => [entry.subject.uuid, entry.subject.name]));

    return [
      { value: ALL, label: 'All subjects' },
      ...[...byUuid].map(([value, label]) => ({ value, label })),
    ];
  }, [classes]);

  const rows = useMemo<ReportRow[]>(() => {
    const needle = search.trim().toLowerCase();

    return classes
      .filter((entry) => classFilter === ALL || entry.classSectionUuid === classFilter)
      .filter((entry) => subjectFilter === ALL || entry.subject.uuid === subjectFilter)
      .filter((entry) => {
        if (!needle) return true;
        const report = entry.report;

        return (
          entry.displayName.toLowerCase().includes(needle) ||
          entry.subject.name.toLowerCase().includes(needle) ||
          (report?.content ?? '').toLowerCase().includes(needle) ||
          (report?.homeworkGiven ?? '').toLowerCase().includes(needle) ||
          (report?.remarks ?? '').toLowerCase().includes(needle)
        );
      })
      .map((entry, index) => ({
        ...entry,
        periodNumbers: entry.periods.map((period) => period.periodNumber).sort((a, b) => a - b),
        serial: index + 1,
      }));
  }, [classes, classFilter, subjectFilter, search]);

  const totalPages = Math.max(1, Math.ceil(rows.length / filters.limit));
  // Clamped rather than stored, so narrowing the day from page three cannot
  // leave the table on a page that no longer exists.
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * filters.limit, currentPage * filters.limit);

  /** The five figures across the top, all counted from the day's own rows. */
  const tiles = useMemo(() => {
    const written = classes.filter((entry) => entry.report !== null);
    const withHomework = written.filter((entry) => entry.report?.homeworkGiven);

    // Counted once a section: a section taught twice today has one roster, and
    // "to 56 students" for 28 children would be a figure nobody can act on.
    const homeworkRoster = new Map(
      withHomework.map((entry) => [
        entry.classSectionUuid,
        registerBySection.get(entry.classSectionUuid)?.totalStudents ?? 0,
      ]),
    );

    // Participation is a band a teacher chose, so the day is summed up as how
    // many lessons landed in each band — never as an average of the numbers
    // the bands are stored as, which would read as something measured.
    const bandCounts = PARTICIPATION_BANDS.map((band) => ({
      band,
      count: written.filter(
        (entry) => participationBand(entry.report?.participationPercent ?? null) === band,
      ).length,
    })).filter((entry) => entry.count > 0);
    const topCount = Math.max(0, ...bandCounts.map((entry) => entry.count));
    const commonest = bandCounts.filter((entry) => entry.count === topCount);

    return {
      written: written.length,
      rated: bandCounts.reduce((sum, entry) => sum + entry.count, 0),
      /** The band most lessons landed in; null when nothing is rated or two tie. */
      commonBand: commonest.length === 1 ? (commonest[0]?.band ?? null) : null,
      bandBreakdown: bandCounts.map((entry) => `${entry.count} ${entry.band.label}`).join(' · '),
      subjectsCovered: new Set(written.map((entry) => entry.subject.uuid)).size,
      subjectsTaught: new Set(classes.map((entry) => entry.subject.uuid)).size,
      homework: withHomework.length,
      homeworkStudents: [...homeworkRoster.values()].reduce((sum, count) => sum + count, 0),
      remarks: written.filter((entry) => entry.report?.remarks).length,
    };
  }, [classes, registerBySection]);

  /**
   * Which class "Add Report" opens on.
   *
   * The first one with nothing written against it, because that is almost
   * always the one the teacher means. Once the whole day is written up it falls
   * back to the first class rather than going dead: the form is also how a
   * report is corrected, and a day that is finished is exactly when somebody
   * comes back to fix one. The class can be changed on the form either way.
   *
   * Only classes already taught are candidates: one still to come has nothing to
   * report, and the server would refuse it.
   */
  const taught = classes.filter((entry) => entry.hasBeenTaught);
  const startOn = taught.find((entry) => entry.report === null) ?? taught[0] ?? null;

  /** Why there is nothing to write up, when there is nothing to write up. */
  const nothingToWriteUp = data?.holiday
    ? `The institute is closed — ${data.holiday.name}`
    : classes.length > 0
      ? 'None of these classes has been taught yet'
      : 'You are not timetabled to teach on this day';

  /**
   * Saves what is on screen, not the whole day.
   *
   * `rows` is already narrowed by the filters and the search box, so the file
   * matches the table the teacher is looking at rather than quietly holding
   * classes they cannot see.
   */
  function exportDay() {
    if (!data) return;

    downloadCsv(`daily-report-${data.date}.csv`, [
      ['Period', 'Time', 'Class', 'Subject', 'Topic covered', 'Homework given', 'Attendance', 'Participation', 'Remarks'],
      ...rows.map((row) => {
        const register = registerBySection.get(row.classSectionUuid);
        const marked = register?.attendance.isMarked ? register : null;

        return [
          row.periodNumbers.join(', '),
          row.periods.map((period) => periodRange(period.startTime, period.endTime)).join(' / '),
          row.displayName,
          row.subject.name,
          row.report?.content ?? '',
          row.report?.homeworkGiven ?? '',
          marked ? `${marked.attendance.present + marked.attendance.late} / ${marked.totalStudents}` : '',
          participationBand(row.report?.participationPercent ?? null)?.label ?? '',
          row.report?.remarks ?? '',
        ];
      }),
    ]);
  }

  const columns: Column<ReportRow>[] = [
    {
      key: 'serial',
      header: '#',
      align: 'center',
      className: 'w-12',
      headerClassName: 'w-12',
      render: (row) => <span className="text-xs font-semibold text-ink-3">{row.serial}</span>,
    },
    {
      key: 'period',
      header: 'Period',
      align: 'center',
      render: (row) => (
        <span className="text-sm font-bold tabular-nums text-ink-2">
          {row.periodNumbers.length > 0 ? row.periodNumbers.join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'time',
      header: 'Time',
      hideOnMobile: true,
      render: (row) => (
        <div className="leading-tight">
          {row.periods.map((period) => (
            <p key={period.uuid} className="whitespace-nowrap text-sm font-medium text-ink-2">
              {periodRange(period.startTime, period.endTime)}
            </p>
          ))}
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => (
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400"
            aria-hidden
          >
            <BookOpen className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold text-ink capitalize">{row.subject.name}</span>
            <span className="block truncate text-2xs font-semibold text-ink-3">
              {row.displayName}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'topic',
      header: 'Topic Covered',
      render: (row) =>
        row.report ? (
          <p className="line-clamp-2 max-w-[16rem] text-sm font-medium text-ink" title={row.report.content}>
            {row.report.content}
          </p>
        ) : (
          <span className="text-sm font-semibold text-ink-3">Not written up</span>
        ),
    },
    {
      key: 'homework',
      header: 'Homework Given',
      hideOnMobile: true,
      render: (row) =>
        row.report?.homeworkGiven ? (
          <p className="line-clamp-2 max-w-[12rem] text-sm font-medium text-ink-2" title={row.report.homeworkGiven}>
            {row.report.homeworkGiven}
          </p>
        ) : (
          NOT_YET
        ),
    },
    {
      key: 'attendance',
      header: 'Attendance',
      align: 'center',
      hideOnMobile: true,
      render: (row) => {
        const register = registerBySection.get(row.classSectionUuid);
        if (!register) return NOT_YET;

        // A register nobody took is not a class that was all absent, so it says
        // so rather than printing "0 / 28".
        if (!register.attendance.isMarked) {
          return <span className="text-2xs font-semibold text-ink-3">Not taken</span>;
        }

        const present = register.attendance.present + register.attendance.late;

        return (
          <span className="whitespace-nowrap text-sm font-bold tabular-nums text-ink">
            {present} / {register.totalStudents}
          </span>
        );
      },
    },
    {
      key: 'participation',
      header: 'Student Participation',
      render: (row) => {
        const band = participationBand(row.report?.participationPercent ?? null);
        return band ? <Badge tone={band.tone}>{band.label}</Badge> : NOT_YET;
      },
    },
    {
      key: 'remarks',
      header: 'Remarks',
      render: (row) =>
        row.report?.remarks ? (
          <p className="line-clamp-2 max-w-[12rem] text-sm font-medium text-ink-2" title={row.report.remarks}>
            {row.report.remarks}
          </p>
        ) : (
          NOT_YET
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'center',
      render: (row) => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            type="button"
            disabled={!row.report}
            onClick={() => openForm(row, 'view')}
            aria-label={`View the report for ${row.displayName}, ${row.subject.name}`}
            title={row.report ? 'View this report' : 'Nothing written up yet'}
            className="grid h-8 w-8 place-items-center rounded-field border border-line bg-surface text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </button>

          <button
            type="button"
            disabled={!canWriteUp(row)}
            onClick={() => openForm(row, 'edit')}
            aria-label={`Write up ${row.displayName}, ${row.subject.name}`}
            title={writeUpTitle(row)}
            className="grid h-8 w-8 place-items-center rounded-field border border-line bg-surface text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </button>

          <Menu
            label={`More actions for ${row.displayName}, ${row.subject.name}`}
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            triggerClassName="h-8 w-8 border border-line bg-surface text-ink-2 hover:border-accent hover:text-accent"
            items={[
              {
                key: 'view',
                label: 'View report',
                icon: <Eye className="h-4 w-4" />,
                disabled: !row.report,
                onSelect: () => openForm(row, 'view'),
              },
              {
                key: 'edit',
                label: row.report ? 'Edit report' : 'Write this class up',
                icon: <PenLine className="h-4 w-4" />,
                disabled: !canWriteUp(row),
                onSelect: () => openForm(row, 'edit'),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const shownDate = data?.date ?? date;

  return (
    <div className="flex flex-col gap-5">
      {/* --- 1. PAGE HEADER --------------------------------------------------- */}
      <header className="card flex flex-col gap-3.5 rounded-[16px] border border-line bg-surface p-4 shadow-2xs xl:flex-row xl:items-center xl:justify-between xl:p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400 sm:h-12 sm:w-12"
            aria-hidden
          >
            <FileText className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-ink sm:text-2xl">Daily Report</h1>
            <p className="mt-0.5 text-xs font-medium text-ink-3 sm:text-sm">
              Record and track your daily class activities, coverage, homework and student updates.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full xl:w-auto">
          <div className="flex items-center gap-1.5 w-full sm:w-auto">
            <div className="flex-1 sm:w-44">
              <DatePicker
                label="Day being reported on"
                hideLabel
                size="sm"
                value={shownDate}
                onChange={(event) => updateFilters({ date: event.target.value })}
                className="h-9 w-full text-[13px] font-semibold"
              />
            </div>

            {/* The arrows move a day at a time, which is how a teacher catches
                up on a day they missed — and they are disabled until the
                server has said which day we are on, or they would step from
                an empty string. */}
            <button
              type="button"
              disabled={!shownDate}
              onClick={() => updateFilters({ date: shiftDay(shownDate, -1) })}
              aria-label="Previous day"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-field border border-line bg-surface text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              disabled={!shownDate}
              onClick={() => updateFilters({ date: shiftDay(shownDate, 1) })}
              aria-label="Next day"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-field border border-line bg-surface text-ink-2 transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {/* Disabled when the day holds no class that has been taught yet —
              never because the day is already written up, which is when a
              teacher is most likely to be coming back to correct one. */}
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={!startOn}
            title={
              startOn
                ? `Write up ${startOn.displayName} · ${startOn.subject.name}`
                : nothingToWriteUp
            }
            onClick={() => startOn && openForm(startOn, 'edit')}
            className="w-full sm:w-auto font-bold text-[14px]"
          >
            Add Report
          </Button>
        </div>
      </header>

      {error ? (
        <QueryError title="Could not load your day" error={error} onRetry={refetch} />
      ) : isLoading ? (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3.5 xl:grid-cols-5">
            {[0, 1, 2, 3, 4].map((key) => (
              <Skeleton key={key} className="h-20 w-full rounded-card" />
            ))}
          </div>
          <Skeleton className="h-96 w-full rounded-card" />
        </>
      ) : data?.holiday ? (
        /* A declared closure, before the empty state: "no classes" is true but
           unhelpful when the reason is that the institute is shut. */
        <div className="card rounded-[16px] p-8">
          <EmptyState
            icon={<CalendarOff className="h-5 w-5" />}
            title={data.holiday.name}
            description={`The institute is closed on ${formatInstituteDate(data.date)}, so there is nothing to report.`}
          />
        </div>
      ) : (
        data && (
          <>
            {/* --- 2. HEADLINE FIGURES ---------------------------------------- */}
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3.5 xl:grid-cols-5">
              <MetricTile
                tone="violet"
                icon={<BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />}
                label="Classes written up"
                value={tiles.written}
                hint={`of ${data.summary.classes} scheduled`}
                trailing={
                  data.summary.classes > 0 ? (
                    <PercentRing
                      value={(tiles.written / data.summary.classes) * 100}
                      label="Classes written up"
                      toneClassName="text-violet-500"
                      size={44}
                    />
                  ) : undefined
                }
              />
              <MetricTile
                tone="rose"
                icon={<FileText className="h-4 w-4 sm:h-5 sm:w-5" />}
                label="Subjects covered"
                value={tiles.subjectsCovered}
                hint={`of ${tiles.subjectsTaught} subjects ${
                  data.isToday ? 'today' : `on ${formatInstituteDate(data.date)}`
                }`}
              />
              <MetricTile
                tone="sky"
                icon={<Pencil className="h-4 w-4 sm:h-5 sm:w-5" />}
                label="Homework Given"
                value={tiles.homework}
                hint={
                  tiles.homeworkStudents > 0
                    ? `to ${tiles.homeworkStudents} students`
                    : 'in these classes'
                }
              />
              <MetricTile
                tone="emerald"
                icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
                label="Student Participation"
                value={
                  tiles.rated === 0 ? '—' : (tiles.commonBand?.label ?? 'Mixed')
                }
                hint={
                  tiles.rated === 0
                    ? 'Nothing rated yet'
                    : `${tiles.bandBreakdown} · ${tiles.rated} of ${data.summary.classes} rated`
                }
              />
              <MetricTile
                className="col-span-2 sm:col-span-1"
                tone="amber"
                icon={<MessageSquare className="h-4 w-4 sm:h-5 sm:w-5" />}
                label="Remarks Added"
                value={tiles.remarks}
                hint="for follow-up"
              />
            </div>

            {/* --- 3. THE DAY'S PERIODS --------------------------------------- */}
            <section className="card overflow-hidden rounded-[16px] border border-line bg-surface shadow-2xs">
              <div className="flex flex-col gap-3 border-b border-line p-4 xl:flex-row xl:items-end">
                <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <Select
                    label="Class"
                    options={classOptions}
                    value={classFilter}
                    onChange={(event) => updateFilters({ class: event.target.value })}
                    className="h-9 text-[13px] font-semibold"
                  />
                  {/* Only worth asking with two subjects to tell apart — the
                      first option is "All subjects". */}
                  {subjectOptions.length > 2 && (
                    <Select
                      label="Subject"
                      options={subjectOptions}
                      value={subjectFilter}
                      onChange={(event) => updateFilters({ subject: event.target.value })}
                      className="h-9 text-[13px] font-semibold"
                    />
                  )}
                  <Input
                    label="Search topics and remarks"
                    type="search"
                    value={search}
                    onChange={(event) => updateFilters({ search: event.target.value })}
                    placeholder="Search topics, remarks..."
                    leftIcon={<Search className="h-3.5 w-3.5" />}
                    className="h-9 w-full text-[13px]"
                  />
                </div>

                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-4 w-4" />}
                  className="h-9 shrink-0 text-[13px] font-semibold w-full sm:w-auto"
                  onClick={exportDay}
                  disabled={rows.length === 0}
                >
                  Export
                </Button>
              </div>

              <Table
                variant="plain"
                columns={columns}
                rows={pageRows}
                rowKey={(row) => classKey(row)}
                empty={
                  <EmptyState
                    icon={<ClipboardList className="h-5 w-5" />}
                    title={
                      classes.length === 0 ? 'No classes on this day' : 'Nothing matches those filters'
                    }
                    description={
                      classes.length === 0
                        ? `You are not timetabled to teach on ${formatInstituteDate(data.date)}. Pick another day, or ask the office if your timetable looks wrong.`
                        : 'Clear the filters or the search box to see the whole day.'
                    }
                  />
                }
              />

              {rows.length > 0 && (
                <Pagination
                  meta={{ page: currentPage, limit: filters.limit, total: rows.length, totalPages }}
                  onPageChange={(next) => setFilters({ page: next })}
                  itemLabel={rows.length === 1 ? 'record' : 'records'}
                  showPageNumbers
                  limitOptions={PAGE_SIZES}
                  onLimitChange={(size) => setFilters({ limit: size, page: 1 })}
                />
              )}
            </section>

            {/* --- 4. THE DAY AT A GLANCE ------------------------------------- */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <SubjectCoverageCard classes={classes} date={data.date} isToday={data.isToday} />
              <DailyAttendanceCard
                sections={daySections}
                date={data.date}
                isToday={data.isToday}
                isLoading={attendance.isLoading}
              />
              <DailyReportNotesPanel
                notes={data.notes}
                noteDate={data.date}
                onChanged={refetch}
              />
            </div>
          </>
        )
      )}

    </div>
  );
}
